import { useEffect, useState } from 'react';
import { isDemoMode } from '../../config/env';
import { dancerEmoji, type Honorific, type PersonVisual } from '../../lib/honorific';
import { api } from '../../services/api';
import './uiAtoms.css';

export function PersonAvatar({
  name,
  honorific,
  photoFileId,
  photoUrl,
  userId,
  color = '#8B6F47',
  size = 'md',
}: {
  name: string;
  honorific?: Honorific | null;
  photoFileId?: string | null;
  photoUrl?: string | null;
  userId?: string;
  color?: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const [src, setSrc] = useState<string | null>(photoUrl ?? null);

  useEffect(() => {
    setSrc(photoUrl ?? null);
    if (photoUrl || !photoFileId || !userId || isDemoMode) return;
    let objectUrl = '';
    let alive = true;
    api.getBlob(`/users/${userId}/photo`).then((url) => {
      if (!alive) {
        URL.revokeObjectURL(url);
        return;
      }
      objectUrl = url;
      setSrc(url);
    }).catch(() => {
      if (alive) setSrc(null);
    });
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [photoUrl, photoFileId, userId]);

  const emoji = dancerEmoji(honorific);

  return (
    <span
      className={`ui-avatar ui-avatar--${size} ${src ? 'ui-avatar--photo' : ''} ${src ? '' : 'ui-avatar--emoji'}`}
      style={src ? undefined : { background: `${color}18`, color }}
      aria-label={name}
    >
      {src ? <img src={src} alt="" /> : emoji}
    </span>
  );
}

export function PersonStack({ people, color }: { people: PersonVisual[]; color?: string }) {
  const shown = people.slice(0, 3);
  if (shown.length === 0) return null;
  return (
    <span className="person-stack" aria-hidden="true">
      {shown.map((person, index) => (
        <PersonAvatar
          key={person._id ?? `${person.name}-${index}`}
          name={person.name}
          honorific={person.honorific}
          photoFileId={person.photoFileId}
          photoUrl={person.photoUrl}
          userId={person._id}
          color={color}
          size="sm"
        />
      ))}
    </span>
  );
}
